import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const team = [
  { name: "Dr. Tāniora Maxwell", role: "Governance, Policy and Research Lead", iwi: "Ngāi Tai | Te Whakatōhea | Ngāti Awa | Ngāti Whātua", description: "An Indigenous research and strategy practitioner with 25 years' experience across governance, policy, and systems design. Advises iwi, public sector, and community organisations on complex decisions requiring cultural authority and analytical rigour. Holds a PhD in Indigenous research and policy.", photo: "https://customer-assets.emergentagent.com/job_tangata-hub/artifacts/wryxaajj_Dr%20T%C4%81niora%20Maxwell.jpeg" },
  { name: "Donna Perese", role: "Chairperson, International Investment and Partnerships", iwi: "Te Whānau ā Apanui | Te Whakatōhea | Ngāti Awa | Ngāti Whare | Ngāti Rangitihi | Samoa: Tufulele, Magiagi", description: "Strategic leader with extensive experience across governance and regional economic development. Former CE of a regional economic development agency and Chamber of Commerce. Supreme Award winner, Economic Development NZ 2023. Holds an Executive MBA.", photo: "https://customer-assets.emergentagent.com/job_tangata-hub/artifacts/41up9tdq_Donna%20Perese.jpeg", imgPos: "center 20%" },
  { name: "Tiana Epati", role: "Director of Engineering and Systems, Pacific Lead", iwi: "Siumu, Iva: Savai'i | Lepa, Ta'elefaga Fagaloa Samoa | Niue", description: "Delivery-focused leader with 15 years across construction, infrastructure, and geotechnical engineering. Leads multi-stakeholder programmes prioritising safety and performance. Holds NZCE, Bachelor of Civil Engineering Technology, and Executive MBA from Massey.", photo: "https://customer-assets.emergentagent.com/job_tangata-hub/artifacts/48x2ucws_Tiana%20Epati.jpeg" },
];

export default function TeamSection() {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [paused, setPaused] = useState(false);
  const [selected, setSelected] = useState(null);

  // Auto-scroll carousel - refs used to avoid stale closures
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let animId;
    const speed = 1.0;
    const tick = () => {
      if (!paused && selected === null && el) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [paused, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const doubled = [...team, ...team];

  const handleCardClick = (memberIndex) => {
    // Toggle: click same card again to deselect and resume
    if (selected === memberIndex) {
      setSelected(null);
    } else {
      setSelected(memberIndex);
    }
  };

  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-10 sm:py-14 overflow-hidden bg-[#45443f]"
    >
      <div ref={ref} className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-white mb-4">
              Our People
            </p>
            <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              Built on <span className="text-white">relationships.</span>
            </h2>
          </motion.div>
        </div>

        {/* Auto-scrolling carousel */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => { setPaused(false); if (selected !== null) return; }}
          className="flex gap-5 overflow-x-hidden pl-6 sm:pl-8 lg:pl-12 pr-12 cursor-pointer"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {doubled.map((member, i) => {
            const realIndex = i % team.length;
            const isSelected = selected === realIndex;
            return (
              <div
                key={`${member.name}-${i}`}
                data-testid={i < team.length ? `team-card-${i}` : undefined}
                onClick={() => handleCardClick(realIndex)}
                className={`flex-shrink-0 w-[260px] sm:w-[340px] bg-[#0E0D0C] border overflow-hidden group transition-all duration-300 ${
                  isSelected ? "border-[#bb321a] ring-1 ring-[#bb321a]" : "border-white/10"
                }`}
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img loading="lazy"
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ objectPosition: member.imgPos || "center top" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
                <div className="p-4 sm:p-5 flex flex-col">
                  {/* Role */}
                  <div className="min-h-[32px] sm:min-h-[36px] flex items-start mb-1">
                    <p className="text-[10px] sm:text-xs font-semibold tracking-[0.12em] uppercase text-white leading-tight">
                      {member.role}
                    </p>
                  </div>
                  {/* Name */}
                  <h3 className="font-['Syne'] text-base sm:text-lg font-bold text-white mb-1">
                    {member.name}
                  </h3>
                  {/* Iwi */}
                  <div className="min-h-[36px] sm:min-h-[48px] flex items-start mb-1">
                    <p className="text-[9px] sm:text-[10px] text-white/40 italic leading-tight">{member.iwi}</p>
                  </div>
                  {/* Description - half gap above */}
                  <p className={`text-white/90 text-xs leading-relaxed ${isSelected ? "" : "line-clamp-3"}`}>
                    {member.description}
                  </p>
                  {isSelected && (
                    <p className="text-white text-xs mt-3 font-semibold">Click again to resume</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-white/30 text-xs text-center mt-4">
          {selected !== null ? "Click the card again to resume scrolling" : "Click a card to read more"}
        </p>
      </div>
    </section>
  );
}
